import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Upload } from 'lucide-react';
import { api } from '../services/api';

/**
 * "I left you a review" claims — the owner's side.
 *
 * Since Oct 26 2026 the claims themselves live on Needs Attention / My Day (one
 * item per claim waiting on him, with Approve / Turn down: NeedsAttention.tsx,
 * services/reviewClaims.attentionItem). What stays here is the button that
 * uploads a screenshot a customer texted him, straight onto their invoice.
 */

/** Upload a screenshot the customer texted you, straight onto their invoice. */
export function UploadReviewScreenshot({ invoiceId, onDone }: { invoiceId: number; onDone?: () => void }) {
  const ref = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();

  async function send(file: File) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('screenshot', file);
      const { data } = await api.post(`/review-claims/invoice/${invoiceId}`, fd);
      toast.success(data?.message || data?.summary?.headline || 'Checked — it is on Needs Attention');
      qc.invalidateQueries('review-claims'); qc.invalidateQueries('attention'); qc.invalidateQueries('invoices');
      onDone?.();
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'Could not check that screenshot');
    } finally { setBusy(false); }
  }

  return (
    <>
      <button onClick={() => ref.current?.click()} disabled={busy}
        className="text-xs px-2.5 py-1 rounded-full border border-gray-200 text-gray-600 hover:border-amber-400 hover:text-amber-700 flex items-center gap-1 disabled:opacity-50">
        <Upload className="h-3 w-3" /> {busy ? 'Checking…' : 'Review screenshot'}
      </button>
      <input ref={ref} type="file" accept="image/*" className="hidden"
        onChange={e => { const f = e.target.files?.[0]; if (f) send(f); e.target.value = ''; }} />
    </>
  );
}
